// tslint:disable: no-bitwise
import styled from '@emotion/styled';
import { Spacer } from '@travelnest/merlin.spacer';
import theme from '@travelnest/merlin.theme';
import { HeadingSmall } from '@travelnest/merlin.typography';
import * as React from 'react';
import { Link } from 'react-router-dom';

export type EventStatus = 'booked' | 'blocked' | 'available' | 'synced';
export enum EventSelectionType {
	BOOKED = 1 << 1,
	BLOCKED = 1 << 2,
	AVAILABLE = 1 << 3,
	SYNCED = 1 << 4,
}

const SubParagraph = styled.div`
	font-size: 0.875rem;
	color: ${theme.palette.greyscale.dark};
`;

const ChangesWillOverwrite = () => <>
	<Spacer spacing="smaller" />
	<SubParagraph>Any changes you make will overwrite this.</SubParagraph>
</>;

const CantChangeBooking: React.FC<{ singular?: boolean }> = ({ singular }) => <>
	<Spacer spacing="smaller" />
	<SubParagraph>You can’t make changes to the booking{singular === true ? '' : 's'} in this selection.</SubParagraph>
</>;

const BookingLink: React.FC<{ propertyId: string, selectedBookingIds: string[] }> = ({ propertyId, selectedBookingIds }) => {
	const numberOfBookings = selectedBookingIds.length;
	const link = numberOfBookings > 1 ? `/property/${propertyId}/bookings` : `/property/${propertyId}/booking/${selectedBookingIds[0]}`;
	return (
		<>{numberOfBookings} <Link to={link}>booking{numberOfBookings === 1 ? '' : 's'}</Link></>
	);
};

const BookingStatus: React.FC<{ propertyId: string, selectedBookingIds: string[] }> = ({ propertyId, selectedBookingIds }) =>
	selectedBookingIds.length === 1
		? <>{' '}There is <BookingLink propertyId={propertyId} selectedBookingIds={selectedBookingIds} /> in these nights.</>
		: (
			<>
				{' '}There are <BookingLink propertyId={propertyId} selectedBookingIds={selectedBookingIds} /> in these nights.
			</>
		);

export const getEventSelectionMessage = (bitwiseSelectionType: number, nights: number, propertyId: string, selectedBookingIds: string[]) => {
	switch (bitwiseSelectionType) {
		case EventSelectionType.AVAILABLE:
			return <HeadingSmall>{(nights > 1 ? 'These nights are' : 'This night is') + ' available for guests to book.'}</HeadingSmall>;
		case EventSelectionType.AVAILABLE | EventSelectionType.BLOCKED:
			return <>
				<HeadingSmall>Some nights are available for guests to book. Other nights have been blocked by you. </HeadingSmall>
				<ChangesWillOverwrite />
			</>;
		case EventSelectionType.AVAILABLE | EventSelectionType.BOOKED:
			return <>
				<HeadingSmall>Some nights are available for guests to book.
					<BookingStatus propertyId={propertyId} selectedBookingIds={selectedBookingIds} />
				</HeadingSmall>
				<CantChangeBooking />
			</>;
		case EventSelectionType.AVAILABLE | EventSelectionType.SYNCED:
			// TODO: ical name interpolated into this message.
			return <>
				<HeadingSmall>Some nights are available for guests to book. Others have been blocked by a synced calendar.</HeadingSmall>
				<ChangesWillOverwrite />
			</>;
		case EventSelectionType.AVAILABLE | EventSelectionType.BLOCKED | EventSelectionType.BOOKED:
			return <>
				<HeadingSmall>Some nights are available for guests to book. Some nights have been blocked by you.
					<BookingStatus propertyId={propertyId} selectedBookingIds={selectedBookingIds} />
				</HeadingSmall>
				<CantChangeBooking />
			</>;
		case EventSelectionType.AVAILABLE | EventSelectionType.BLOCKED | EventSelectionType.SYNCED:
			return <>
				<HeadingSmall>
					Some nights are available for guests to book. Some nights have been blocked by you. Others have been blocked by a synced calendar.
				</HeadingSmall>
				<ChangesWillOverwrite />
			</>;
		case EventSelectionType.AVAILABLE | EventSelectionType.BOOKED | EventSelectionType.SYNCED:
			return <>
				<HeadingSmall>Some nights are available for guests to book. Some nights are blocked by a synced calendar.
					<BookingStatus propertyId={propertyId} selectedBookingIds={selectedBookingIds} />
				</HeadingSmall>
				<CantChangeBooking />
			</>;
		case EventSelectionType.AVAILABLE | EventSelectionType.BLOCKED | EventSelectionType.BOOKED | EventSelectionType.SYNCED:
			return <>
				<HeadingSmall>
					Some nights are available for guests to book. Some nights are blocked by a synced calendar. Some nights have been blocked by you.
					<BookingStatus propertyId={propertyId} selectedBookingIds={selectedBookingIds} />
				</HeadingSmall>
				<CantChangeBooking />
			</>;
		case EventSelectionType.BLOCKED:
			return <HeadingSmall>{(nights > 1 ? 'These nights are' : 'This night is') + ' blocked by you.'}</HeadingSmall>;
		case EventSelectionType.BLOCKED | EventSelectionType.BOOKED:
			return <>
				<HeadingSmall>Some nights have been blocked by you.
					<BookingStatus propertyId={propertyId} selectedBookingIds={selectedBookingIds} />
				</HeadingSmall>
				<CantChangeBooking />
			</>;
		case EventSelectionType.BLOCKED | EventSelectionType.SYNCED:
			return <>
				<HeadingSmall>Some nights have been blocked by you. Others have been blocked by a synced calendar.</HeadingSmall>
				<ChangesWillOverwrite />
			</>;
		case EventSelectionType.BLOCKED | EventSelectionType.BOOKED | EventSelectionType.SYNCED:
			return <>
				<HeadingSmall>Some nights have been blocked by you. Others have been blocked by a synced calendar.
					<BookingStatus propertyId={propertyId} selectedBookingIds={selectedBookingIds} />
				</HeadingSmall>
				<CantChangeBooking />
			</>;
		case EventSelectionType.BOOKED:
			return <HeadingSmall>
				{
					nights > 1 ? 'These nights have' : 'This night has'
				} <BookingLink propertyId={propertyId} selectedBookingIds={selectedBookingIds} />.
				<CantChangeBooking singular={nights === 1} />
			</HeadingSmall>;
		case EventSelectionType.BOOKED | EventSelectionType.SYNCED:
			return <>
				<HeadingSmall>Some nights have been blocked by a synced calendar.
					<BookingStatus propertyId={propertyId} selectedBookingIds={selectedBookingIds} />
				</HeadingSmall>
				<CantChangeBooking />
			</>;
		case EventSelectionType.SYNCED:
			return <>
				<HeadingSmall>
					{(nights > 1 ? 'These nights have' : 'This night has') + ' been blocked by your synced calendar.'}
				</HeadingSmall>
				<Spacer spacing="smaller" />
				<SubParagraph>
					Any changes you make will overwrite these synced events.
					Dates you make available in your Travelnest calendar must also be made available in the synced calendar.
				</SubParagraph>
			</>
				;
		default:
			return <></>;
	}
};

const StyledEventSelectionText = styled.div`
	margin: ${theme.layout.spacing.large};
`;

const StyledEventBlock = styled.span<{ type: string }>`
	display: inline-block;
	border-radius: ${theme.layout.radius.squared};
	color: ${theme.colours.text.white};
	font-weight: 500;
	font-size: 12px;
	text-align: center;
	line-height: 1.5em;
	padding: 0 15px;
	margin-right: 5px;
	${({ type }) => {
		if (type === 'synced')
			return `
			background: repeating-linear-gradient(
				-45deg,
				#BFD1F8,
				#BFD1F8 15px,
				#D4DFF9 15px,
				#D4DFF9 25px
			);
				color: #444444;
			`;
		if (type === 'booked')
			return `
				background-image: url('/71eb904/assets/images/cal-booked.svg');
				background-color: #1262f8;
				background-position: 0;
				color: #FFFFFF;
			`;
		if (type === 'blocked')
			return `
				background: repeating-linear-gradient(
					-45deg,
					#505050,
					#505050 15px,
					#666666 15px,
						#666666 25px
					);
					color: #ffffff;
			`;
		return `
			background-color: #FFFFFF;
			background-position: 0;
			color: ${theme.colours.neutral.darkest};
			border: 1px solid ${theme.colours.neutral.darker};
		`;
	}}
`;

const EventBlock: React.FC<{ type: string }> = ({ type }) => <StyledEventBlock type={type}>
	{type[0].toUpperCase()}{type.slice(1).toLowerCase()}
</StyledEventBlock>;

interface IEventSelectionStatusProps {
	selectedEventStatuses: EventStatus[];
	selectedBookingIds: string[];
	nights: number;
	propertyId: string;
}

const toSelectionType = (statuses: EventStatus[]) => {
	let selectionType = 0;

	if (statuses?.includes('blocked')) selectionType |= EventSelectionType.BLOCKED;
	if (statuses?.includes('booked')) selectionType |= EventSelectionType.BOOKED;
	if (statuses?.includes('available')) selectionType |= EventSelectionType.AVAILABLE;
	if (statuses?.includes('synced')) selectionType |= EventSelectionType.SYNCED;

	return selectionType;
};

export const EventSelectionStatus: React.FC<IEventSelectionStatusProps> = ({ selectedEventStatuses, selectedBookingIds, nights, propertyId }) => {
	const selectionType = toSelectionType(selectedEventStatuses);

	return nights > 0
		? (
			<StyledEventSelectionText>
				{(['available', 'booked', 'blocked', 'synced'] as EventStatus[]).map((t) => selectedEventStatuses?.includes(t) && <EventBlock type={t} />)}
				<Spacer spacing="small" />
				{getEventSelectionMessage(selectionType, nights, propertyId, selectedBookingIds)}
			</StyledEventSelectionText>
		)
		: <></>;
};
